<template>
  <Card>
    <CardHeading
      :heading="heading"
      :currentItems="testimonials.length"
      :showCapacity="!editingTestimonials"
      :maxItems="1"
      :showButton="!editingTestimonials"
      :showCancelButton="editingTestimonials"
      buttonText="Add testimonial"
      @button-click="handleNewTestimonial"
      @cancel-click="editingTestimonials = false"
    />
    <Callout
      v-if="testimonials.length == 1"
      message="Testimonial limit reached"
    />
    <div v-if="!editingTestimonials && testimonials[0]">
      <ProfileContentItem
        v-for="(testimonial, i) in testimonials"
        :reorderable="false"
        :title="getTitle(testimonial)"
        @edit-click="
          editingTestimonials = { new: false, data: { ...testimonial } }
        "
        @delete-click="
          $emit('delete-content', {
            item: { ...testimonial, order: 0 }, // there will only be one max
            section: 'testimonials',
            modalTitle: 'Delete testimonial?',
            modalDescription:
              'This action cannot be undone, are you sure you want to delete this testimonial?',
          })
        "
        :description="getDescription(testimonial)"
        :key="i + 1"
      />
    </div>
    <CardText
      v-if="!editingTestimonials && !testimonials[0]"
      text="No testimonials have been added."
    />
    <CardFormWrapper v-if="editingTestimonials">
      <div class="grid" style="margin-bottom: 0.5rem">
        <div
          v-for="option in mainInputOptions"
          :key="option"
          class="grid__cell col4-of-12"
        >
          <Input
            :value="editingTestimonials.data[getOption(option, 'id')]"
            @input-change="
              handleEdit({ section: 'editingTestimonials', data: $event })
            "
            :name="getOption(option, 'id')"
            :label="getOption(option, 'label')"
            :placeholder="getOption(option, 'placeholder')"
          />
        </div>
      </div>
      <div class="grid">
        <div class="grid__cell col12-of-12">
          <Input
            :value="editingTestimonials.data[getOption('quote', 'id')]"
            @input-change="
              handleEdit({ section: 'editingTestimonials', data: $event })
            "
            height="240px"
            type="textarea"
            :limit="500"
            :name="getOption('quote', 'id')"
            :label="getOption('quote', 'label')"
            :required="true"
            :placeholder="getOption('quote', 'placeholder')"
          />
        </div>
      </div>
      <div class="flex" style="justify-content: flex-end; margin: 1rem 0 0 0">
        <Button @button-click="editingTestimonials = false" bType="secondary">
          Cancel
        </Button>
        <Button
          bMargin="left"
          :bDisabled="!testimonialFormComplete"
          @button-click="
            handleDoneClick({
              item: 'editingTestimonials',
              section: 'testimonials',
            })
          "
        >
          Done
        </Button>
      </div>
    </CardFormWrapper>
  </Card>
</template>

<script>
import Vue from 'vue';
import Input from '@/components/DesignSystem/Input';
import Card from '@/components/DesignSystem/Card';
import CardHeading from '@/components/DesignSystem/CardHeading';
import Callout from '@/components/DesignSystem/Callout';
import CardFormWrapper from '@/components/DesignSystem/CardFormWrapper';
import CardText from '@/components/DesignSystem/CardText';
import ProfileContentItem from '@/components/Shared/ProfileContentItem';
import Button from '@/components/DesignSystem/Button';
import { inputOptions } from '@/components/Shared/ContentSection/QuoteInputOptions';

export default {
  components: {
    Card,
    CardHeading,
    Callout,
    CardFormWrapper,
    Input,
    Button,
    CardText,
    ProfileContentItem,
  },
  props: {
    namespace: String,
    heading: {
      type: String,
      default: 'Testimonials',
    },
    servicePartnerProfile: Boolean,
    agencyOPP: Boolean,
    reviewsEnabled: Boolean,
    storeInfo: Object,
  },
  data() {
    return {
      editingTestimonials: false,
      mainInputOptions: ['name', 'position', 'company'],
    };
  },
  computed: {
    testimonials: {
      get() {
        return this.$store.getters[
          `${this.namespace}/${this.storeInfo ? this.storeInfo.get : 'quotes'}`
        ];
      },
      set(payload) {
        this.$store.commit(
          `${this.namespace}/${
            this.storeInfo ? this.storeInfo.set : 'setQuotes'
          }`,
          payload
        );
      },
    },
    testimonialFormComplete() {
      if (this.editingTestimonials && this.editingTestimonials.data) {
        let type = '';
        if (this.servicePartnerProfile) {
          type = 'servicePartnerProfile';
        } else if (this.agencyOPP) {
          type = 'agencyOPP';
        }
        const inputIds = Object.values(this.inputInfo(type)).map(o => o.id);
        const checkAllFieldsEntered = inputIds.every(
          id => this.editingTestimonials.data[id]
        );
        if (checkAllFieldsEntered) {
          return true;
        }
      }
      return false;
    },
  },
  methods: {
    ...inputOptions,
    handleEdit(payload) {
      Vue.set(
        this[payload.section]['data'],
        payload.data.key,
        payload.data.value
      );
      this.$forceUpdate();
    },

    handleDoneClick({ item, section }) {
      if (this[item]['new']) {
        this.addItemForTestimonial({ section, data: this[item]['data'] });
      } else {
        this.editItemForTestimonial({ section, data: this[item]['data'] });
      }
      return (this[item] = false);
    },

    addItemForTestimonial(payload) {
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.add : 'addItemForTestimonial'
        }`,
        payload
      );
    },

    editItemForTestimonial(payload) {
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.edit : 'editItemForTestimonial'
        }`,
        payload
      );
    },

    deleteItemForTestimonial(payload) {
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.delete : 'deleteItemForTestimonial'
        }`,
        payload
      );
    },
    handleNewTestimonial() {
      if (this.reviewsEnabled) return '';
      this.editingTestimonials = {
        new: true,
        data: {},
      };
      if (this.servicePartnerProfile || this.agencyOPP) {
        this.editingTestimonials.data = {
          authorName: '',
          companyName: '',
          testimonialText: '',
          position: '',
        };
      } else {
        this.editingTestimonials.data = {
          fullName: '',
          companyName: '',
          quoteText: '',
          jobTitle: '',
        };
      }
    },
    getTitle(value) {
      if (this.servicePartnerProfile || this.agencyOPP) {
        return this.getTitleFormat(
          value.authorName,
          value.position,
          value.companyName
        );
      }
      return this.getTitleFormat(
        value.fullName,
        value.jobTitle,
        value.companyName
      );
    },
    getDescription(value) {
      if (this.servicePartnerProfile || this.agencyOPP)
        return value['testimonialText'];
      return value['quoteText'];
    },

    getOption(inputOption, attribute) {
      let type = '';
      if (this.servicePartnerProfile) {
        type = 'servicePartnerProfile';
      } else if (this.agencyOPP) {
        type = 'agencyOPP';
      }
      return this.inputInfo(type)[inputOption][attribute];
    },
  },
};
</script>
